/**
 * Data Hub — Benchmarked tab.
 *
 * Ported from `components/pipeline/benchmark/benchmark-console.tsx`. Per-metric
 * distribution bars with the percentile envelope, plus Category / Confidence
 * filters and Min-DQS slider.
 */
import { useMemo, useState } from "react";

import DropdownSelector from "../../Components/DropdownSelector";
import { PercentileBar } from "../components/PercentileBar";
import { TabCard, TabError, TabLoading } from "../components/TabState";
import type { BenchmarkTier } from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

const TIER_STYLES: Record<BenchmarkTier, string> = {
  2: "bg-green-100 text-green-800",
  1: "bg-blue-100 text-blue-800",
  0: "bg-gray-100 text-gray-700",
};

const TIER_LEGEND: { tier: BenchmarkTier; text: string }[] = [
  { tier: 2, text: "org-calculated (own data)" },
  { tier: 1, text: "industry / global default (fallback)" },
  { tier: 0, text: "seeded rubric (placeholder)" },
];

export function BenchmarkedTab() {
  const [dataset] = useActiveDataset();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getBenchmarks(),
    [dataset]
  );
  const [category, setCategory] = useState("all");
  const [confidence, setConfidence] = useState("all");
  const [minDqs, setMinDqs] = useState(0);

  const categories = useMemo(
    () => Array.from(new Set((data ?? []).map(b => b.category))).sort(),
    [data]
  );

  const rows = useMemo(() => {
    let out = data ?? [];
    if (category !== "all") out = out.filter(b => b.category === category);
    if (confidence !== "all")
      out = out.filter(b => b.confidence === confidence);
    if (minDqs > 0) out = out.filter(b => b.dqs >= minDqs);
    return out;
  }, [data, category, confidence, minDqs]);

  if (loading) return <TabLoading label="Loading benchmarks…" />;
  if (error) return <TabError error={error} />;

  return (
    <TabCard
      title="Benchmarks per metric"
      subtitle={`${rows.length} of ${data?.length ?? 0} metrics`}
      actions={
        <>
          <DropdownSelector
            label="Category"
            selected={category}
            options={[
              { value: "all", label: "All categories" },
              ...categories.map(c => ({ value: c, label: c })),
            ]}
            onSelect={setCategory}
            enableScroll
          />
          <DropdownSelector
            label="Confidence"
            selected={confidence}
            options={[
              { value: "all", label: "All confidence" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
            ]}
            onSelect={setConfidence}
          />
          <div className="flex min-w-[140px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5">
            <label
              htmlFor="benchmark-min-dqs"
              className="shrink-0 text-xs font-medium text-gray-600"
            >
              Min DQS
            </label>
            <input
              id="benchmark-min-dqs"
              type="range"
              min={0}
              max={100}
              step={1}
              value={minDqs}
              onChange={e => setMinDqs(Number(e.target.value))}
              className="w-20 accent-[#974ae4]"
              aria-label="Minimum DQS"
            />
            <span className="w-7 text-left text-xs tabular-nums text-gray-700">
              {minDqs}
            </span>
          </div>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-gray-500">
        <span className="flex items-center gap-2">
          higher is better
          <span
            className="inline-block h-2 w-16 rounded-full"
            style={{
              background: "linear-gradient(90deg,#dc2626,#f59e0b,#16a34a)",
            }}
          />
        </span>
        <span className="flex items-center gap-2">
          lower is better
          <span
            className="inline-block h-2 w-16 rounded-full"
            style={{
              background: "linear-gradient(90deg,#16a34a,#f59e0b,#dc2626)",
            }}
          />
        </span>
        <span className="flex flex-wrap items-center gap-2">
          Tiers:
          {TIER_LEGEND.map(t => (
            <span key={t.tier} className="flex items-center gap-1">
              <span
                className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${TIER_STYLES[t.tier]}`}
              >
                tier {t.tier}
              </span>
              {t.text}
            </span>
          ))}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="m-0 py-10 text-center text-sm text-gray-500">
          No benchmarks match these filters.
        </p>
      ) : (
        <div className="max-h-[min(55vh,560px)] divide-y divide-gray-100 overflow-y-auto overscroll-contain">
          {rows.map(b => (
            <div key={b.metric_id} className="py-4 first:pt-0">
              <div className="mb-2 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="m-0 text-sm font-semibold text-gray-900">
                    {b.metric_name}
                  </p>
                  <p className="m-0 font-mono text-[11px] text-gray-400">
                    {b.metric_id} · {b.category}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
                  <span className="tabular-nums">
                    mean {b.mean.toLocaleString()} ± {b.stddev.toLocaleString()}
                  </span>
                  {b.tier != null && (
                    <span
                      className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${TIER_STYLES[b.tier]}`}
                    >
                      tier {b.tier}
                    </span>
                  )}
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    source: {b.source}
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    confidence: {b.confidence}
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    DQS {b.dqs}
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">
                    n={b.n}
                  </span>
                </div>
              </div>
              <PercentileBar
                min={b.min}
                p10={b.p10}
                p25={b.p25}
                p50={b.p50}
                p75={b.p75}
                p90={b.p90}
                max={b.max}
                higherIsBetter={b.higher_is_better}
                uom={b.uom}
              />
            </div>
          ))}
        </div>
      )}
    </TabCard>
  );
}

export default BenchmarkedTab;
